import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  Alert,
  Spinner as BsSpinner,
  Modal,
  InputGroup,
  Tabs,
  Tab,
} from "react-bootstrap";
import SectionContainer from "../../components/UI/SectionContainer";
import Spinner from "../../components/UI/Spinner";
import alertDashboardService from "../../services/alertDashboardService";
import { openAlert, openConfirm } from "../../utils/common";

/**
 * 채널 설정 관리 페이지
 * IA-08 알림 채널 설정 및 테스트
 */
const ChannelSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [channelHealth, setChannelHealth] = useState(null);
  const [error, setError] = useState(null);
  const [testingChannel, setTestingChannel] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);

  // 설정 편집 관련 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [editChannel, setEditChannel] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // 데이터 로드
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await alertDashboardService.getChannelSettings();

      if (result.success) {
        setSettings(result.data.settings);
        setChannelHealth(result.data.channel_health);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("채널 설정을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // 채널 테스트 실행
  const handleTest = async (channel) => {
    if (channel === "email" || channel === "sms") {
      setSelectedChannel(channel);
      setTestRecipient("");
      setShowTestModal(true);
      return;
    }

    await executeTest(channel, "");
  };

  const executeTest = async (channel, recipient) => {
    setTestingChannel(channel);
    setTestResult(null);
    setShowTestModal(false);

    try {
      const result = await alertDashboardService.testChannel(
        channel,
        recipient
      );
      setTestResult({
        channel,
        success: result.success,
        message: result.success ? result.data?.message : result.message,
        data: result.data,
      });

      if (result.success) {
        openAlert({
          title: "성공",
          description: result.data?.message || "테스트 발송이 완료되었습니다.",
        });
      } else {
        openAlert({
          title: "실패",
          description: result.message || "테스트 발송에 실패했습니다.",
        });
      }
    } catch (err) {
      setTestResult({
        channel,
        success: false,
        message: "테스트 중 오류가 발생했습니다.",
      });
      openAlert({
        title: "오류",
        description: "테스트 중 오류가 발생했습니다.",
      });
    } finally {
      setTestingChannel(null);
      // 상태 새로고침
      loadSettings();
    }
  };

  // 설정 편집 모달 열기
  const handleEdit = (channel) => {
    setEditChannel(channel);
    // 현재 설정값으로 폼 초기화
    const currentSettings = settings?.[channel] || {};
    setEditForm({ ...currentSettings });
    setShowEditModal(true);
  };

  // 설정 저장
  const handleSaveSettings = async () => {
    if (!editChannel) return;

    setSaving(true);
    try {
      const result = await alertDashboardService.updateChannelSettings(
        editChannel,
        editForm
      );

      if (result.success) {
        openAlert({
          title: "성공",
          description: result.data?.message || "설정이 저장되었습니다.",
        });
        setShowEditModal(false);
        loadSettings(); // 새로고침
      } else {
        openAlert({
          title: "실패",
          description: result.message || "설정 저장에 실패했습니다.",
        });
      }
    } catch (err) {
      openAlert({
        title: "오류",
        description: "설정 저장 중 오류가 발생했습니다.",
      });
    } finally {
      setSaving(false);
    }
  };

  // 폼 입력 핸들러
  const handleFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // 상태 Badge
  const getStatusBadge = (status) => {
    const variants = {
      HEALTHY: { bg: "success", text: "정상" },
      WARNING: { bg: "warning", text: "경고" },
      CRITICAL: { bg: "danger", text: "위험" },
      DISABLED: { bg: "secondary", text: "비활성" },
      UNKNOWN: { bg: "dark", text: "알 수 없음" },
    };
    const config = variants[status] || variants["UNKNOWN"];
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  // 채널 아이콘
  const getChannelIcon = (type) => {
    const icons = {
      email: "📧",
      slack: "💬",
      sms: "📱",
      push: "🔔",
    };
    return icons[type] || "📢";
  };

  // 채널 이름
  const getChannelName = (type) => {
    const names = {
      email: "이메일 (SMTP)",
      slack: "Slack",
      sms: "SMS",
      push: "Push 알림",
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <SectionContainer title="채널 설정 관리">
        <div className="text-center py-5">
          <Spinner />
          <p className="mt-3">채널 설정을 불러오는 중...</p>
        </div>
      </SectionContainer>
    );
  }

  if (error) {
    return (
      <SectionContainer title="채널 설정 관리">
        <Alert variant="danger">
          <Alert.Heading>오류 발생</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadSettings}>
            다시 시도
          </Button>
        </Alert>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer
      title="채널 설정 관리"
      description="알림 채널 설정 확인 및 테스트 발송"
    >
      <Container fluid className="px-0">
        {/* 채널 상태 요약 */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  <strong>채널 상태 요약</strong>
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={loadSettings}
                >
                  새로고침
                </Button>
              </Card.Header>
              <Card.Body>
                <Row>
                  {["email", "slack", "sms", "push"].map((channel) => (
                    <Col key={channel} md={3} sm={6} className="mb-3">
                      <Card
                        className={`h-100 ${
                          channelHealth?.[channel]?.status === "HEALTHY"
                            ? "border-success"
                            : channelHealth?.[channel]?.status === "DISABLED"
                            ? "border-secondary"
                            : "border-warning"
                        }`}
                      >
                        <Card.Body className="text-center">
                          <div className="fs-2 mb-2">
                            {getChannelIcon(channel)}
                          </div>
                          <h6>{getChannelName(channel)}</h6>
                          <div className="mb-2">
                            {getStatusBadge(
                              channelHealth?.[channel]?.status || "UNKNOWN"
                            )}
                          </div>
                          {channelHealth?.[channel]?.response_time > 0 && (
                            <small className="text-muted">
                              응답시간: {channelHealth[channel].response_time}ms
                            </small>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 이메일 설정 */}
        <Row className="mb-4">
          <Col lg={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  {getChannelIcon("email")} <strong>이메일 (SMTP) 설정</strong>
                </span>
                {getStatusBadge(
                  settings?.email?.enabled ? "HEALTHY" : "DISABLED"
                )}
              </Card.Header>
              <Card.Body>
                <table className="table table-sm table-borderless mb-3">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: "40%" }}>
                        SMTP 서버
                      </td>
                      <td>
                        <code>{settings?.email?.smtp_host || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">포트</td>
                      <td>
                        <code>{settings?.email?.smtp_port || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">보안</td>
                      <td>
                        <code>
                          {settings?.email?.smtp_secure?.toUpperCase() || "-"}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">발신자</td>
                      <td>
                        <code>{settings?.email?.smtp_from || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">관리자 이메일</td>
                      <td>
                        <code>{settings?.email?.admin_email || "-"}</code>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleEdit("email")}
                  >
                    설정 편집
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleTest("email")}
                    disabled={
                      !settings?.email?.enabled || testingChannel === "email"
                    }
                  >
                    {testingChannel === "email" ? (
                      <>
                        <BsSpinner size="sm" className="me-1" /> 테스트 중...
                      </>
                    ) : (
                      "테스트 발송"
                    )}
                  </Button>
                </div>
                {settings?.email?.source && (
                  <small className="text-muted d-block mt-2">
                    설정 소스:{" "}
                    {settings.email.source === "database" ? "DB" : "환경변수"}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Slack 설정 */}
          <Col lg={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  {getChannelIcon("slack")} <strong>Slack 설정</strong>
                </span>
                {getStatusBadge(
                  settings?.slack?.enabled ? "HEALTHY" : "DISABLED"
                )}
              </Card.Header>
              <Card.Body>
                <table className="table table-sm table-borderless mb-3">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: "40%" }}>
                        Webhook URL
                      </td>
                      <td>
                        <code
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {settings?.slack?.webhook_url || "-"}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">채널</td>
                      <td>
                        <code>{settings?.slack?.channel || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">사용자명</td>
                      <td>
                        <code>{settings?.slack?.username || "-"}</code>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleEdit("slack")}
                  >
                    설정 편집
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleTest("slack")}
                    disabled={
                      !settings?.slack?.enabled || testingChannel === "slack"
                    }
                  >
                    {testingChannel === "slack" ? (
                      <>
                        <BsSpinner size="sm" className="me-1" /> 테스트 중...
                      </>
                    ) : (
                      "테스트 발송"
                    )}
                  </Button>
                </div>
                {settings?.slack?.source && (
                  <small className="text-muted d-block mt-2">
                    설정 소스:{" "}
                    {settings.slack.source === "database" ? "DB" : "환경변수"}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* SMS 설정 */}
          <Col lg={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  {getChannelIcon("sms")} <strong>SMS 설정</strong>
                </span>
                {getStatusBadge(
                  settings?.sms?.enabled ? "HEALTHY" : "DISABLED"
                )}
              </Card.Header>
              <Card.Body>
                <table className="table table-sm table-borderless mb-3">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: "40%" }}>
                        제공자
                      </td>
                      <td>
                        <code>{settings?.sms?.provider || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">서비스 ID</td>
                      <td>
                        <code
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {settings?.sms?.service_id || "-"}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">발신번호</td>
                      <td>
                        <code>{settings?.sms?.sender_id || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">관리자 전화번호</td>
                      <td>
                        <code>{settings?.sms?.admin_phone || "-"}</code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">분당 발송 제한</td>
                      <td>
                        <code>{settings?.sms?.rate_limit || "-"}건</code>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleEdit("sms")}
                  >
                    설정 편집
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleTest("sms")}
                    disabled={
                      !settings?.sms?.enabled || testingChannel === "sms"
                    }
                  >
                    {testingChannel === "sms" ? (
                      <>
                        <BsSpinner size="sm" className="me-1" /> 테스트 중...
                      </>
                    ) : (
                      "테스트 발송"
                    )}
                  </Button>
                </div>
                {settings?.sms?.source && (
                  <small className="text-muted d-block mt-2">
                    설정 소스:{" "}
                    {settings.sms.source === "database" ? "DB" : "환경변수"}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Push 설정 */}
          <Col lg={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  {getChannelIcon("push")} <strong>Push 알림 설정</strong>
                </span>
                {getStatusBadge(
                  settings?.push?.enabled ? "HEALTHY" : "DISABLED"
                )}
              </Card.Header>
              <Card.Body>
                <table className="table table-sm table-borderless mb-3">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: "40%" }}>
                        FCM 설정
                      </td>
                      <td>
                        {settings?.push?.fcm_configured ? (
                          <Badge bg="success">설정됨</Badge>
                        ) : (
                          <Badge bg="secondary">미설정</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">상태</td>
                      <td>
                        {settings?.push?.enabled ? (
                          <Badge bg="success">활성화</Badge>
                        ) : (
                          <Badge bg="secondary">비활성화</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Credentials 경로</td>
                      <td>
                        <code
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                        >
                          {settings?.push?.credentials_path || "-"}
                        </code>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleTest("push")}
                  disabled={
                    !settings?.push?.enabled || testingChannel === "push"
                  }
                >
                  {testingChannel === "push" ? (
                    <>
                      <BsSpinner size="sm" className="me-1" /> 테스트 중...
                    </>
                  ) : (
                    "연결 테스트"
                  )}
                </Button>
                {settings?.push?.source && (
                  <small className="text-muted d-block mt-2">
                    설정 소스:{" "}
                    {settings.push.source === "database" ? "DB" : "환경변수"}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 테스트 결과 */}
        {testResult && (
          <Row className="mb-4">
            <Col>
              <Alert
                variant={testResult.success ? "success" : "danger"}
                dismissible
                onClose={() => setTestResult(null)}
              >
                <Alert.Heading>
                  {getChannelIcon(testResult.channel)}{" "}
                  {getChannelName(testResult.channel)} 테스트 결과
                </Alert.Heading>
                <p className="mb-0">{testResult.message}</p>
                {testResult.data?.sent_at && (
                  <small className="text-muted">
                    발송 시간: {testResult.data.sent_at}
                  </small>
                )}
              </Alert>
            </Col>
          </Row>
        )}

        {/* 안내 메시지 */}
        <Row>
          <Col>
            <Alert variant="info">
              <Alert.Heading>채널 설정 안내</Alert.Heading>
              <ul className="mb-0">
                <li>
                  <strong>DB 설정 우선:</strong> DB에 저장된 설정이 환경변수보다
                  우선 적용됩니다.
                </li>
                <li>
                  설정 편집 버튼을 클릭하여 DB에 설정을 저장할 수 있습니다.
                </li>
                <li>테스트 발송 시 실제 메시지가 발송됩니다.</li>
                <li>SMS 발송은 요금이 발생할 수 있습니다.</li>
              </ul>
            </Alert>
          </Col>
        </Row>
      </Container>

      {/* 테스트 수신자 입력 모달 */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {getChannelIcon(selectedChannel)} {getChannelName(selectedChannel)}{" "}
            테스트 발송
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              {selectedChannel === "email"
                ? "수신자 이메일"
                : "수신자 전화번호"}
            </Form.Label>
            <Form.Control
              type={selectedChannel === "email" ? "email" : "tel"}
              placeholder={
                selectedChannel === "email"
                  ? "example@domain.com"
                  : "01012345678"
              }
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
            />
            <Form.Text className="text-muted">
              비워두면 기본 관리자{" "}
              {selectedChannel === "email" ? "이메일" : "전화번호"}로
              발송됩니다.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => executeTest(selectedChannel, testRecipient)}
          >
            테스트 발송
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 설정 편집 모달 */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        fullscreen="md-down"
        dialogClassName="channel-settings-modal"
        style={{ paddingTop: "5vh" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {getChannelIcon(editChannel)} {getChannelName(editChannel)} 설정
            편집
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {editChannel === "email" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>SMTP 서버</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={editForm.smtp_host || ""}
                  onChange={(e) =>
                    handleFormChange("smtp_host", e.target.value)
                  }
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>포트</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="465"
                      value={editForm.smtp_port || ""}
                      onChange={(e) =>
                        handleFormChange("smtp_port", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>보안</Form.Label>
                    <Form.Select
                      value={editForm.smtp_secure || "ssl"}
                      onChange={(e) =>
                        handleFormChange("smtp_secure", e.target.value)
                      }
                    >
                      <option value="ssl">SSL</option>
                      <option value="tls">TLS</option>
                      <option value="">없음</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>SMTP 사용자</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="user@gmail.com"
                  value={editForm.smtp_user || ""}
                  onChange={(e) =>
                    handleFormChange("smtp_user", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>SMTP 비밀번호</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="비워두면 기존 값 유지"
                  value={editForm.smtp_password || ""}
                  onChange={(e) =>
                    handleFormChange("smtp_password", e.target.value)
                  }
                />
                <Form.Text className="text-muted">
                  비워두면 기존 비밀번호가 유지됩니다.
                </Form.Text>
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>발신자 이메일</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="noreply@example.com"
                      value={editForm.smtp_from || ""}
                      onChange={(e) =>
                        handleFormChange("smtp_from", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>발신자 이름</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="GlucoSOUND"
                      value={editForm.smtp_from_name || ""}
                      onChange={(e) =>
                        handleFormChange("smtp_from_name", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>관리자 이메일</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="admin@example.com"
                  value={editForm.admin_email || ""}
                  onChange={(e) =>
                    handleFormChange("admin_email", e.target.value)
                  }
                />
              </Form.Group>
            </>
          )}

          {editChannel === "slack" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Webhook URL</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://hooks.slack.com/services/..."
                  value={editForm.webhook_url || ""}
                  onChange={(e) =>
                    handleFormChange("webhook_url", e.target.value)
                  }
                />
                <Form.Text className="text-muted">
                  Slack 앱 설정에서 Incoming Webhook URL을 복사하세요.
                </Form.Text>
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>채널</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="#alerts"
                      value={editForm.channel || ""}
                      onChange={(e) =>
                        handleFormChange("channel", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>사용자명</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="GlucoSOUND"
                      value={editForm.username || ""}
                      onChange={(e) =>
                        handleFormChange("username", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {editChannel === "sms" && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="sms-enabled"
                  label="SMS 활성화"
                  checked={
                    editForm.enabled === true || editForm.enabled === "true"
                  }
                  onChange={(e) =>
                    handleFormChange(
                      "enabled",
                      e.target.checked ? "true" : "false"
                    )
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>제공자</Form.Label>
                <Form.Select
                  value={editForm.provider || "naver_cloud"}
                  onChange={(e) => handleFormChange("provider", e.target.value)}
                >
                  <option value="naver_cloud">네이버 클라우드 SENS</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>서비스 ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ncp:sms:kr:..."
                  value={editForm.service_id || ""}
                  onChange={(e) =>
                    handleFormChange("service_id", e.target.value)
                  }
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Access Key</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="비워두면 기존 값 유지"
                      value={editForm.api_key || ""}
                      onChange={(e) =>
                        handleFormChange("api_key", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Secret Key</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="비워두면 기존 값 유지"
                      value={editForm.api_secret || ""}
                      onChange={(e) =>
                        handleFormChange("api_secret", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>발신번호</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="01012345678"
                      value={editForm.sender_id || ""}
                      onChange={(e) =>
                        handleFormChange("sender_id", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>관리자 전화번호</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="01012345678"
                      value={editForm.admin_phone || ""}
                      onChange={(e) =>
                        handleFormChange("admin_phone", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>분당 발송 제한</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="10"
                  value={editForm.rate_limit || ""}
                  onChange={(e) =>
                    handleFormChange("rate_limit", e.target.value)
                  }
                />
              </Form.Group>
            </>
          )}

          {editChannel === "push" && (
            <>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="push-enabled"
                  label="Push 알림 활성화"
                  checked={
                    editForm.enabled === true || editForm.enabled === "true"
                  }
                  onChange={(e) =>
                    handleFormChange(
                      "enabled",
                      e.target.checked ? "true" : "false"
                    )
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>FCM Server Key</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="비워두면 기존 값 유지"
                  value={editForm.fcm_server_key || ""}
                  onChange={(e) =>
                    handleFormChange("fcm_server_key", e.target.value)
                  }
                />
                <Form.Text className="text-muted">
                  Firebase Console에서 서버 키를 복사하세요.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <>
                <BsSpinner size="sm" className="me-1" /> 저장 중...
              </>
            ) : (
              "저장"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </SectionContainer>
  );
};

export default ChannelSettingsPage;
